/* eslint-disable camelcase */

import { useProject } from '@atlassian/jira-business-entity-project-hook/src/index.tsx';
import { useIssueTypesAndFields } from '@atlassian/jira-business-entity-project/src/services/issue-types-and-fields/index.tsx';
import { useFilteredViewContextInput } from '@atlassian/jira-business-filters/src/controllers/default-query-input/index.tsx';
import { useCacheActions } from '@atlassian/jira-business-list-handlers/src/controllers/list-cache/main.tsx';
import type { AGGCompliantAPI } from '@atlassian/jira-business-list-handlers/src/controllers/pagination/types.tsx';
import { useOverview } from '@atlassian/jira-router-resources-business-overview-details/src/index.tsx';
import type { IssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import { fieldIds } from '../../common/constants.tsx';
import { getVariables } from '../../common/utils/get-variables/index.tsx';
import { useIssueParentMap } from '../../common/utils/use-issue-parent-map.tsx';
import type {
	JWMTimelineViewItems_jira,
	JWMTimelineViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node as ItemsNode,
	JWMTimelineViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_issueLinks as IssueLinks,
} from '../get-timeline-view-issues/__generated_apollo__/JWMTimelineViewItems';
import { TIMELINE_VIEW_ITEMS_QUERY } from '../get-timeline-view-issues/gql.tsx';

export const useUpdateIssueLinksInCache = () => {
	const { updateItemsInCache } = useCacheActions();
	const [{ issueParentMap }] = useIssueParentMap();
	const { hierarchyLevels } = useIssueTypesAndFields({ issueOperation: 'VIEW' });
	const { quickFiltersJql, hierarchyParent, jqlContext, isHidingDoneItems } =
		useFilteredViewContextInput({
			view: 'timeline',
		});

	const hierarchyLevel = hierarchyLevels[0]?.level;

	const { data: overview } = useOverview();
	const isOverview = overview != null && Array.isArray(overview.projects);

	let projectKey: string | undefined;
	if (!isOverview) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { key } = useProject();
		projectKey = key;
	}

	const updateIssueLinksInCache = ({
		issueId,
		issueLinks,
	}: {
		issueId?: IssueId;
		issueLinks?: IssueLinks | null;
	}) => {
		if (!issueId) {
			return;
		}

		const parentIssueId = issueParentMap.get(String(issueId));
		const hasParentIssue = parentIssueId != null && hierarchyParent != null;

		updateItemsInCache<ItemsNode, AGGCompliantAPI<JWMTimelineViewItems_jira>>({
			query: TIMELINE_VIEW_ITEMS_QUERY,
			variables: {
				...getVariables({
					jqlContext,
					fieldIds,
					quickFiltersJql,
					isHidingDoneItems,
					hierarchyLevel,
					hierarchyParent,
					...(hasParentIssue
						? {
								hierarchyParent: undefined,
								parentIssue: {
									hierarchyType: hierarchyParent,
									issueId: parentIssueId,
								},
							}
						: {}),
					...(projectKey ? { projectIdOrKey: projectKey } : {}),
				}),
			},
			issueIds: [issueId],
			updateItem: (existingIssue) => ({
				...existingIssue,
				issueLinks: issueLinks ?? null,
			}),
		});
	};

	return { updateIssueLinksInCache };
};
